import { useQuery } from '@tanstack/react-query';

import {
  fetchReceptionWorkflowScopeResponse,
  type ReceptionWorkflowScopeResponse,
} from '../../api/receptionWorkflowDetails';
import { useUserProfile } from '../../contexts/useUserProfile';
import { useNavigationScope } from '../../navigation';
import { ReceptionWorkflowHttpError } from './receptionWorkflowNavigation';

export function useReceptionWorkflowScope(enabled = true) {
  const { scopeId } = useNavigationScope();
  const { profileResolved, userBranch } = useUserProfile();
  const profileIdentity = `${scopeId}:${profileResolved === false ? 'pending' : 'resolved'}:${userBranch.trim().toLowerCase()}`;
  const query = useQuery({
    queryKey: ['reception', 'workflow-scope', profileIdentity],
    queryFn: async ({ signal }): Promise<ReceptionWorkflowScopeResponse> => {
      const response = await fetchReceptionWorkflowScopeResponse(signal);
      if (!response.ok) throw new ReceptionWorkflowHttpError(
        response.status,
        response.status === 403
          ? 'Your reception account does not have a valid branch scope.'
          : 'Failed to resolve reception access scope.',
      );
      return response.json() as Promise<ReceptionWorkflowScopeResponse>;
    },
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    retry: false,
    enabled: enabled && profileResolved !== false,
    placeholderData: undefined,
  });

  const scopeReady = query.isSuccess && !query.isPlaceholderData;
  const scopeToken = scopeReady
    ? [
        profileIdentity,
        query.data.employee_id ?? 'anonymous',
        query.data.employee_branch ?? '',
        query.data.can_select_all_branches ? 1 : 0,
        ...query.data.allowed_branches.map((value) => value.trim().toLowerCase()).sort(),
      ].join('|')
    : null;

  return {
    ...query,
    data: scopeReady ? query.data : undefined,
    isSuccess: scopeReady,
    scopeToken,
    profileIdentity,
  };
}
